import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import {
  X,
  Clock,
  User,
  MapPin,
  FileText,
  Calendar,
  Check,
  ArrowRightCircle,
  AlertCircle,
  Pencil,
  Trash2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Appointment } from '@/services/appointmentService';
import { Link } from 'react-router-dom';

interface AppointmentDetailPanelProps {
  appointment: Appointment;
  onEdit: () => void;
  onDelete: () => void;
  onConvertToPractice: () => void;
  onStatusChange: (status: string) => void;
  onClose: () => void;
}

const statusConfig: Record<string, { label: string; color: string; bgColor: string; icon: any }> = {
  SCHEDULED: { label: 'Programmato', color: 'text-blue-700', bgColor: 'bg-blue-50 border-blue-200', icon: Clock },
  CONFIRMED: { label: 'Confermato', color: 'text-green-700', bgColor: 'bg-green-50 border-green-200', icon: Check },
  IN_PROGRESS: { label: 'In corso', color: 'text-yellow-700', bgColor: 'bg-yellow-50 border-yellow-200', icon: ArrowRightCircle },
  COMPLETED: { label: 'Completato', color: 'text-gray-700', bgColor: 'bg-gray-50 border-gray-200', icon: Check },
  CANCELLED: { label: 'Annullato', color: 'text-red-700', bgColor: 'bg-red-50 border-red-200', icon: X },
  NO_SHOW: { label: 'Non presentato', color: 'text-orange-700', bgColor: 'bg-orange-50 border-orange-200', icon: AlertCircle },
};

export default function AppointmentDetailPanel({
  appointment,
  onEdit,
  onDelete,
  onConvertToPractice,
  onStatusChange,
  onClose,
}: AppointmentDetailPanelProps) {
  const status = statusConfig[appointment.status] || statusConfig.SCHEDULED;
  const StatusIcon = status.icon;

  const formatDateTime = (dateString: string) => {
    return format(new Date(dateString), "HH:mm", { locale: it });
  };

  const formatFullDate = (dateString: string) => {
    return format(new Date(dateString), "EEEE d MMMM yyyy", { locale: it });
  };

  return (
    <Card className="h-full border-r shadow-lg">
      <CardHeader className="pb-3 border-b">
        <div className="flex items-start justify-between">
          <div className="flex-1 pr-4">
            <h2 className="text-xl font-bold text-gray-900 mb-1">
              {appointment.title}
            </h2>
            <p className="text-sm text-gray-500">
              {formatFullDate(appointment.startTime)}
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="shrink-0"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>
      </CardHeader>

      <CardContent className="py-4 space-y-5 overflow-y-auto max-h-[calc(100vh-300px)]">
        {/* Orario */}
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
            <Clock className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <p className="text-sm text-gray-500">Orario</p>
            <p className="font-semibold text-lg">
              {formatDateTime(appointment.startTime)} - {formatDateTime(appointment.endTime)}
            </p>
          </div>
        </div>

        {/* Stato */}
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${status.bgColor}`}>
            <StatusIcon className={`h-5 w-5 ${status.color}`} />
          </div>
          <div className="flex-1">
            <p className="text-sm text-gray-500 mb-1">Stato</p>
            <Select
              value={appointment.status}
              onValueChange={onStatusChange}
            >
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="SCHEDULED">Programmato</SelectItem>
                <SelectItem value="CONFIRMED">Confermato</SelectItem>
                <SelectItem value="IN_PROGRESS">In corso</SelectItem>
                <SelectItem value="COMPLETED">Completato</SelectItem>
                <SelectItem value="CANCELLED">Annullato</SelectItem>
                <SelectItem value="NO_SHOW">Non presentato</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Assegnatario */}
        {appointment.user && (
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
              <User className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Assegnatario</p>
              <p className="font-medium">
                {appointment.user.firstName} {appointment.user.lastName}
              </p>
            </div>
          </div>
        )}

        {/* Cliente */}
        {appointment.client && (
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
              <User className="h-5 w-5 text-indigo-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Cliente</p>
              <Link
                to={`/clients/${appointment.client.id}`}
                className="font-medium text-blue-600 hover:underline"
              >
                {appointment.client.firstName} {appointment.client.lastName}
              </Link>
              {appointment.client.phone && (
                <p className="text-sm text-gray-500">{appointment.client.phone}</p>
              )}
            </div>
          </div>
        )}

        {/* Pratica */}
        {appointment.practice && (
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
              <FileText className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Pratica</p>
              <Link
                to={`/practices/${appointment.practice.id}`}
                className="font-medium text-blue-600 hover:underline"
              >
                {appointment.practice.title}
              </Link>
              {appointment.practice.type && (
                <Badge
                  variant="outline"
                  className="ml-2 text-xs"
                  style={{
                    borderColor: appointment.practice.type.color || undefined,
                    color: appointment.practice.type.color || undefined,
                  }}
                >
                  {appointment.practice.type.code}
                </Badge>
              )}
            </div>
          </div>
        )}

        {/* Luogo */}
        {appointment.location && (
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-rose-100 flex items-center justify-center">
              <MapPin className="h-5 w-5 text-rose-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Luogo</p>
              <p className="font-medium">{appointment.location}</p>
            </div>
          </div>
        )}

        {/* Descrizione */}
        {appointment.description && (
          <div className="pt-2 border-t">
            <p className="text-sm text-gray-500 mb-1">Descrizione</p>
            <p className="text-gray-700">{appointment.description}</p>
          </div>
        )}

        {/* Note */}
        {appointment.notes && (
          <div className="pt-2 border-t">
            <p className="text-sm text-gray-500 mb-1">Note</p>
            <p className="text-gray-700 italic">{appointment.notes}</p>
          </div>
        )}

        {/* Pulsanti Azione */}
        <div className="pt-4 border-t space-y-2">
          <Button
            variant="outline"
            className="w-full justify-start"
            onClick={onEdit}
          >
            <Pencil className="h-4 w-4 mr-2" />
            Modifica appuntamento
          </Button>

          {!appointment.practiceId && appointment.clientId && (
            <Button
              variant="outline"
              className="w-full justify-start text-blue-600 border-blue-200 hover:bg-blue-50"
              onClick={onConvertToPractice}
            >
              <FileText className="h-4 w-4 mr-2" />
              Converti in Pratica
            </Button>
          )}

          <Button
            variant="outline"
            className="w-full justify-start text-red-600 border-red-200 hover:bg-red-50"
            onClick={onDelete}
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Elimina appuntamento
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
